import "./styles.css";
import chevronDownIcon from "lucide-static/icons/chevron-down.svg?raw";
import clockIcon from "lucide-static/icons/clock.svg?raw";
import coffeeIcon from "lucide-static/icons/coffee.svg?raw";
import handHeartIcon from "lucide-static/icons/hand-heart.svg?raw";
import heartIcon from "lucide-static/icons/heart.svg?raw";
import locateIcon from "lucide-static/icons/locate-fixed.svg?raw";
import mapPinIcon from "lucide-static/icons/map-pin.svg?raw";
import moonIcon from "lucide-static/icons/moon.svg?raw";
import pawPrintIcon from "lucide-static/icons/paw-print.svg?raw";
import searchIcon from "lucide-static/icons/search.svg?raw";
import starIcon from "lucide-static/icons/star.svg?raw";
import sunIcon from "lucide-static/icons/sun.svg?raw";
import walletIcon from "lucide-static/icons/wallet.svg?raw";
import xIcon from "lucide-static/icons/x.svg?raw";
import {
  formatTodayHoursWithDay,
  formatWeekHours,
  isOpenAt,
} from "../shared/opening-hours";
import { formatPriceRange } from "../shared/price-range";
import type {
  MapDataResponse,
  MapVenueDto,
  PublicConfigResponse,
} from "../shared/types";

type TimeFilter = "now" | "09:00" | "21:00" | "";

type Filters = {
  search: string;
  favoritesOnly: boolean;
  openAt: TimeFilter;
  allowsDogs: boolean;
};

type State = {
  config: PublicConfigResponse | null;
  venues: MapVenueDto[];
  selectedVenue: MapVenueDto | null;
  user: MapDataResponse["user"];
  filters: Filters;
  map: google.maps.Map | null;
  markers: google.maps.marker.AdvancedMarkerElement[];
  locationMarker: google.maps.Circle | null;
  loading: boolean;
  timeMenuOpen: boolean;
};

const appElement = document.querySelector<HTMLDivElement>("#app");
if (!appElement) {
  throw new Error("#app is missing");
}
const app = appElement;

const state: State = {
  config: null,
  venues: [],
  selectedVenue: null,
  user: null,
  filters: {
    search: "",
    favoritesOnly: false,
    openAt: "",
    allowsDogs: false,
  },
  map: null,
  markers: [],
  locationMarker: null,
  loading: true,
  timeMenuOpen: false,
};

const icons = {
  chevronDown: chevronDownIcon,
  clock: clockIcon,
  coffee: coffeeIcon,
  handHeart: handHeartIcon,
  heart: heartIcon,
  locate: locateIcon,
  mapPin: mapPinIcon,
  moon: moonIcon,
  pawPrint: pawPrintIcon,
  search: searchIcon,
  star: starIcon,
  sun: sunIcon,
  wallet: walletIcon,
  x: xIcon,
};

function icon(svg: string, className = "") {
  const classes = ["ui-icon", className].filter(Boolean).join(" ");
  const markup = svg.replace(
    /<svg\b/,
    '<svg aria-hidden="true" focusable="false"',
  );

  if (markup.includes('class="')) {
    return markup.replace(/class="([^"]*)"/, `class="$1 ${classes}"`);
  }

  return markup.replace(/<svg\b/, `<svg class="${classes}"`);
}

const timeFilterOptions: Array<{
  value: Exclude<TimeFilter, "">;
  label: string;
  menuLabel: string;
  icon: string;
}> = [
  {
    value: "now",
    label: "Открыто сейчас",
    menuLabel: "Открыто сейчас",
    icon: icons.clock,
  },
  {
    value: "09:00",
    label: "В 9:00",
    menuLabel: "Работает в 9:00",
    icon: icons.sun,
  },
  {
    value: "21:00",
    label: "В 21:00",
    menuLabel: "Работает в 21:00",
    icon: icons.moon,
  },
];

const auth = {
  get token() {
    return localStorage.getItem("cm_session_token");
  },
  set token(value: string | null) {
    if (value) localStorage.setItem("cm_session_token", value);
    else localStorage.removeItem("cm_session_token");
  },
};

void boot();

async function boot() {
  renderShell();
  try {
    state.config = await fetchJson<PublicConfigResponse>("/api/config");
    initTelegram();
    await authenticateTelegram();
    await loadMapData();
    state.loading = false;
    renderShell();
    await initMap();
    renderMarkers();
    selectVenueFromUrl();
  } catch (error) {
    state.loading = false;
    renderError(error instanceof Error ? error.message : String(error));
  }
}

function selectVenueFromUrl() {
  const id = Number(new URLSearchParams(window.location.search).get("venue"));
  if (!Number.isInteger(id)) return;
  const venue = state.venues.find((item) => item.id === id);
  if (venue) {
    selectVenue(venue);
    clearVenueFromUrl();
  }
}

function clearVenueFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("venue")) return;

  url.searchParams.delete("venue");
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, "", nextUrl || "/");
}

function initTelegram() {
  const webApp = window.Telegram?.WebApp;
  if (!webApp) return;
  webApp.ready();
  webApp.expand();
}

async function authenticateTelegram() {
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) return;

  const result = await fetchJson<{
    success: boolean;
    token: string;
    user: MapDataResponse["user"];
  }>("/api/auth/telegram", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ initData }),
  });

  if (result.success) {
    auth.token = result.token;
    state.user = result.user;
  }
}

async function loadMapData() {
  const headers: Record<string, string> = {};
  if (auth.token) headers.authorization = `Bearer ${auth.token}`;
  const data = await fetchJson<MapDataResponse>("/api/map-data", { headers });
  state.venues = data.venues;
  state.user = data.user ?? state.user;
}

function filteredVenues() {
  const search = state.filters.search.trim().toLowerCase();
  return state.venues.filter((venue) => {
    if (search) {
      const haystack = [venue.name, venue.brandName, venue.address]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    if (state.filters.favoritesOnly && !venue.isFavorite) return false;
    if (state.filters.allowsDogs && !venue.allowsDogs) return false;
    if (state.filters.openAt) {
      const date = new Date();
      if (state.filters.openAt !== "now") {
        const [hour, minute] = state.filters.openAt.split(":").map(Number);
        date.setHours(hour, minute, 0, 0);
      }
      if (!isOpenAt(venue.openingHours, date)) return false;
    }
    return true;
  });
}

function renderShell() {
  if (!document.querySelector(".map-shell")) {
    app.innerHTML = `
    <main class="map-shell">
      <section id="toolbar" class="toolbar" aria-label="Map filters"></section>
      <div id="map" class="map-canvas" aria-label="Coffee map"></div>
      <section id="list-panel" class="list-panel" aria-label="Coffee shops"></section>
      <button id="locate-button" class="floating-button locate-button" title="Найти меня" aria-label="Найти меня">${icon(icons.locate)}</button>
      <a class="floating-button donate-button" href="https://tiny.keepz.me/trshn6kb" target="_blank" rel="noreferrer" title="Поддержать проект" aria-label="Поддержать проект">${icon(icons.handHeart)}</a>
      <section id="details" class="details-host" aria-live="polite"></section>
    </main>
  `;
    document.querySelector("#locate-button")?.addEventListener("click", () => {
      void locateUser();
    });
    document.addEventListener("click", closeTimeMenu);
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") closeTimeMenu();
    });
  }

  renderToolbar();
  renderVenueList();
  renderDetails();
}

function renderToolbar() {
  const toolbar = document.querySelector("#toolbar");
  if (!toolbar) return;
  toolbar.innerHTML = `
    <div class="search-row">
      <label class="search-field" aria-label="Поиск кофейни">
        ${icon(icons.search)}
        <input id="search-input" class="search-input" placeholder="Поиск кофейни..." value="${escapeAttr(state.filters.search)}" />
      </label>
      <span class="count-pill">${filteredVenues().length} кофеен</span>
    </div>
    <div class="chips" role="group" aria-label="Filters">
      ${state.user ? chip("favorites", "Избранные", state.filters.favoritesOnly, "", icons.heart) : ""}
      ${renderTimeFilter()}
      ${chip("dogs", "С питомцами", state.filters.allowsDogs, "", icons.pawPrint)}
      ${hasFilters() ? chip("reset", "Сброс", false, "reset", icons.x) : ""}
    </div>
  `;
  bindToolbarEvents();
}

function renderVenueList() {
  const panel = document.querySelector("#list-panel");
  if (!panel) return;
  const venues = filteredVenues();
  panel.innerHTML = `
    <div class="list-header">
      <b>Кофейни</b>
      <span>${venues.length}</span>
    </div>
    <div class="list-scroll">
      ${venues
        .map(
          (
            venue,
          ) => `<button class="venue-row ${state.selectedVenue?.id === venue.id ? "selected" : ""}" data-venue-id="${venue.id}">
            <span>
              <b>${escapeHtml(venue.name)}</b>
              <small>${escapeHtml(venue.address)}</small>
            </span>
            ${venue.rating ? `<em>${venue.rating}</em>` : ""}
          </button>`,
        )
        .join("")}
    </div>
  `;

  panel
    .querySelectorAll<HTMLButtonElement>("[data-venue-id]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const venue = state.venues.find(
          (item) => item.id === Number(button.dataset.venueId),
        );
        if (venue) selectVenue(venue);
      });
    });
}

function renderDetails() {
  const host = document.querySelector("#details");
  if (!host) return;
  const venue = state.selectedVenue;
  if (!venue) {
    host.innerHTML = "";
    return;
  }

  const week = formatWeekHours(venue.openingHours);
  const hasOpeningHours =
    Array.isArray(venue.openingHours) && venue.openingHours.length > 0;
  const todayHours = formatTodayHoursWithDay(venue.openingHours);
  const priceRange = formatPriceRange(venue.priceRange);
  host.innerHTML = `
    <div class="drawer-backdrop" data-close-details></div>
    <article class="venue-details">
      <button class="close-button" data-close-details aria-label="Закрыть">${icon(icons.x)}</button>
      <div class="details-scroll">
        <header class="details-header">
          <div>
            <h1>${escapeHtml(venue.name)}</h1>
            ${venue.brandName ? `<p>${escapeHtml(venue.brandName)}</p>` : ""}
          </div>
          ${state.user ? `<button class="favorite-button ${venue.isFavorite ? "active" : ""}" data-favorite aria-label="${venue.isFavorite ? "Убрать из избранного" : "Добавить в избранное"}">${icon(icons.heart)}</button>` : ""}
        </header>
        ${renderGallery(venue)}
        <dl class="details-list">
          <div>${detailLabel("Адрес", icons.mapPin)}<dd>${escapeHtml(venue.address)}</dd></div>
          ${renderHours(todayHours, week, hasOpeningHours)}
          ${venue.rating ? `<div>${detailLabel("Рейтинг", icons.star)}<dd>${venue.rating}${venue.userRatingCount ? ` (${venue.userRatingCount})` : ""}</dd></div>` : ""}
          ${priceRange ? `<div>${detailLabel("Цены", icons.wallet)}<dd>${escapeHtml(priceRange)}</dd></div>` : ""}
          ${venue.allowsDogs ? `<div>${detailLabel("Питомцы", icons.pawPrint)}<dd>Можно с питомцами</dd></div>` : ""}
        </dl>
        ${renderChainSwitcher(venue)}
        ${venue.description ? `<p class="description">${escapeHtml(venue.description)}</p>` : ""}
        <div class="action-row">
          ${venue.googleMapsUrl ? `<a class="action-button primary" href="${escapeAttr(venue.googleMapsUrl)}" target="_blank" rel="noreferrer">Google Maps</a>` : ""}
          ${venue.instagramUrl ? `<a class="action-button secondary" href="${escapeAttr(venue.instagramUrl)}" target="_blank" rel="noreferrer">Instagram</a>` : ""}
        </div>
      </div>
    </article>
  `;

  host.querySelectorAll("[data-close-details]").forEach((element) => {
    element.addEventListener("click", () => {
      state.selectedVenue = null;
      renderShell();
      renderMarkers();
    });
  });

  host.querySelector("[data-favorite]")?.addEventListener("click", () => {
    void toggleFavorite(venue);
  });

  host
    .querySelectorAll<HTMLButtonElement>("[data-chain-venue-id]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const target = state.venues.find(
          (item) => item.id === Number(button.dataset.chainVenueId),
        );
        if (target) selectVenue(target);
      });
    });
}

function renderHours(
  todayHours: string,
  week: Array<{ day: string; hours: string; isToday: boolean }>,
  hasOpeningHours: boolean,
) {
  if (!hasOpeningHours) {
    return `<div>${detailLabel("Время", icons.clock)}<dd>${escapeHtml(todayHours)}</dd></div>`;
  }

  return `<div class="hours-list-item">
    ${detailLabel("Время", icons.clock)}
    <dd>
      <details class="hours-details">
        <summary>
          <span>${escapeHtml(todayHours)}</span>
          <span class="hours-toggle" aria-hidden="true">${icon(icons.chevronDown)}</span>
        </summary>
        <div class="week-schedule">
          ${week
            .map(
              (item) =>
                `<div class="hours-day ${item.isToday ? "is-today" : ""}">
                  <span>${escapeHtml(item.day)}</span>
                  <span>${escapeHtml(item.hours)}</span>
                </div>`,
            )
            .join("")}
        </div>
      </details>
    </dd>
  </div>`;
}

function detailLabel(label: string, svgIcon: string) {
  return `<dt>${icon(svgIcon)}<span>${escapeHtml(label)}</span></dt>`;
}

function renderChainSwitcher(venue: MapVenueDto) {
  const venues = chainVenues(venue);
  if (venues.length < 2) return "";

  return `<section class="chain-switcher" aria-label="Кофейни этой сети">
    <span class="chain-title">Сеть${venue.brandName ? `: ${escapeHtml(venue.brandName)}` : ""}</span>
    <div class="chain-scroll">
      ${venues
        .map(
          (item) => `<button
            class="chain-venue ${item.id === venue.id ? "active" : ""}"
            data-chain-venue-id="${item.id}"
            type="button"
          >
            <span>${escapeHtml(item.name)}</span>
            <small>${escapeHtml(item.address)}</small>
          </button>`,
        )
        .join("")}
    </div>
  </section>`;
}

function chainVenues(venue: MapVenueDto): MapVenueDto[] {
  if (venue.brandId === null && !venue.brandName) return [];

  const venues = state.venues
    .filter((item) => {
      if (venue.brandId !== null && item.brandId !== null) {
        return item.brandId === venue.brandId;
      }
      return item.brandName !== null && item.brandName === venue.brandName;
    })
    .sort((a, b) => a.name.localeCompare(b.name, "ru"));

  return [
    ...venues.filter((item) => item.id === venue.id),
    ...venues.filter((item) => item.id !== venue.id),
  ];
}

function renderGallery(venue: MapVenueDto) {
  if (venue.media.length === 0) return "";
  return `<div class="gallery">${venue.media
    .slice(0, 6)
    .map(
      (media) =>
        `<img src="${escapeAttr(media.publicUrl)}" alt="${escapeAttr(media.alt ?? venue.name)}" loading="lazy">`,
    )
    .join("")}</div>`;
}

async function initMap() {
  const canvas = document.querySelector<HTMLDivElement>("#map");
  if (!canvas || !state.config?.googleMapsBrowserApiKey) {
    renderNoMapFallback();
    return;
  }

  await loadGoogleMaps(state.config.googleMapsBrowserApiKey);
  state.map = new google.maps.Map(canvas, {
    center: { lat: 41.7041632604971, lng: 44.791644792630215 },
    zoom: 13,
    mapId: "ee599e5abf0e090d",
    disableDefaultUI: true,
    clickableIcons: false,
    gestureHandling: "greedy",
    backgroundColor: "#f5f5f5",
    styles: [
      { featureType: "poi", stylers: [{ visibility: "off" }] },
      { featureType: "transit", stylers: [{ visibility: "off" }] },
    ],
  });
}

function renderMarkers() {
  if (!state.map || !window.google?.maps?.marker) return;
  for (const marker of state.markers) {
    marker.map = null;
  }
  state.markers = [];

  for (const venue of filteredVenues()) {
    const element = document.createElement("button");
    element.className = `map-marker ${venue.isFavorite ? "favorite" : ""} ${state.selectedVenue?.id === venue.id ? "selected" : ""}`;
    element.type = "button";
    element.title = venue.name;
    element.innerHTML = icon(venue.isFavorite ? icons.heart : icons.coffee);
    element.addEventListener("click", () => selectVenue(venue));

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: state.map,
      position: { lat: venue.lat, lng: venue.lng },
      content: element,
      title: venue.name,
    });
    state.markers.push(marker);
  }
}

function selectVenue(venue: MapVenueDto) {
  state.selectedVenue = venue;
  if (state.map) {
    state.map.panTo({ lat: venue.lat, lng: venue.lng });
    if ((state.map.getZoom() ?? 0) < 15) {
      state.map.setZoom(
        window.matchMedia("(max-width: 760px)").matches ? 16 : 15,
      );
    }
  }
  renderShell();
  renderMarkers();
  void recordVenueView(venue);
}

// Fire-and-forget: log that this venue was opened. Works for regular opens and
// for chain-switcher switches since both go through selectVenue().
function recordVenueView(venue: MapVenueDto) {
  const headers: Record<string, string> = {
    "content-type": "application/json",
  };
  if (auth.token) headers.authorization = `Bearer ${auth.token}`;
  fetch("/api/map-visits", {
    method: "POST",
    headers,
    body: JSON.stringify({ venueId: venue.id }),
  }).catch(() => {});
}

function bindToolbarEvents() {
  const toolbar = document.querySelector("#toolbar");

  document
    .querySelector<HTMLInputElement>("#search-input")
    ?.addEventListener("input", (event) => {
      state.filters.search = (event.target as HTMLInputElement).value;
      document
        .querySelector(".count-pill")
        ?.replaceChildren(
          document.createTextNode(`${filteredVenues().length} кофеен`),
        );
      renderVenueList();
      renderDetails();
      renderMarkers();
    });

  document
    .querySelectorAll<HTMLButtonElement>("[data-chip]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        const chipName = button.dataset.chip;
        if (chipName === "favorites")
          state.filters.favoritesOnly = !state.filters.favoritesOnly;
        if (chipName === "dogs")
          state.filters.allowsDogs = !state.filters.allowsDogs;
        if (chipName === "reset") {
          state.filters = {
            search: "",
            favoritesOnly: false,
            openAt: "",
            allowsDogs: false,
          };
          state.timeMenuOpen = false;
        }
        renderShell();
        renderMarkers();
      });
    });

  toolbar
    ?.querySelector<HTMLButtonElement>("[data-time-filter-toggle]")
    ?.addEventListener("click", (event) => {
      event.stopPropagation();
      state.timeMenuOpen = !state.timeMenuOpen;
      renderToolbar();
    });

  toolbar
    ?.querySelector(".time-dropdown")
    ?.addEventListener("click", (event) => {
      event.stopPropagation();
    });

  toolbar
    ?.querySelectorAll<HTMLButtonElement>("[data-time-option]")
    .forEach((button) => {
      button.addEventListener("click", () => {
        state.filters.openAt = (button.dataset.timeOption ?? "") as TimeFilter;
        state.timeMenuOpen = false;
        renderShell();
        renderMarkers();
      });
    });

  toolbar
    ?.querySelector<HTMLButtonElement>("[data-time-clear]")
    ?.addEventListener("click", () => {
      state.filters.openAt = "";
      state.timeMenuOpen = false;
      renderShell();
      renderMarkers();
    });
}

async function toggleFavorite(venue: MapVenueDto) {
  if (!auth.token) {
    window.Telegram?.WebApp?.showAlert?.(
      "Избранное доступно в Telegram Mini App.",
    );
    return;
  }

  const method = venue.isFavorite ? "DELETE" : "POST";
  await fetchJson(`/api/venues/${venue.id}/favorite`, {
    method,
    headers: { authorization: `Bearer ${auth.token}` },
  });
  venue.isFavorite = !venue.isFavorite;
  await loadMapData();
  state.selectedVenue =
    state.venues.find((item) => item.id === venue.id) ?? null;
  renderShell();
  renderMarkers();
}

async function locateUser() {
  const position = await getCurrentPosition();
  if (!state.map) return;
  if (state.locationMarker) {
    state.locationMarker.setMap(null);
  }
  state.locationMarker = new google.maps.Circle({
    map: state.map,
    center: position,
    radius: 16,
    fillColor: "#4285F4",
    fillOpacity: 0.75,
    strokeColor: "#ffffff",
    strokeOpacity: 1,
    strokeWeight: 2,
  });
  state.map.panTo(position);
  state.map.setZoom(16);
}

function getCurrentPosition(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    const requestLocation = window.Telegram?.WebApp?.requestLocation;
    if (requestLocation) {
      requestLocation((location) => {
        if (location) {
          resolve({ lat: location.latitude, lng: location.longitude });
        } else {
          browserLocation(resolve, reject);
        }
      });
      return;
    }
    browserLocation(resolve, reject);
  });
}

function browserLocation(
  resolve: (point: { lat: number; lng: number }) => void,
  reject: (error: unknown) => void,
) {
  navigator.geolocation.getCurrentPosition(
    (position) =>
      resolve({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      }),
    reject,
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
  );
}

function renderNoMapFallback() {
  const canvas = document.querySelector("#map");
  if (!canvas) return;
  canvas.innerHTML =
    '<div class="empty-map">Google Maps key is not configured. Venue list is still available.</div>';
}

function renderError(message: string) {
  app.innerHTML = `<main class="error-screen"><h1>CoffeeMap</h1><p>${escapeHtml(message)}</p></main>`;
}

function hasFilters() {
  return (
    state.filters.search ||
    state.filters.favoritesOnly ||
    state.filters.openAt ||
    state.filters.allowsDogs
  );
}

function chip(
  id: string,
  label: string,
  active: boolean,
  extraClass = "",
  svgIcon = "",
) {
  return `<button class="chip ${active ? "active" : ""} ${extraClass}" data-chip="${id}" type="button">${svgIcon ? icon(svgIcon) : ""}<span>${escapeHtml(label)}</span></button>`;
}

function renderTimeFilter() {
  const activeOption = timeFilterOptions.find(
    (option) => option.value === state.filters.openAt,
  );

  return `<div class="dropdown-container time-filter ${state.timeMenuOpen ? "open" : ""}">
    <button
      class="chip time-filter-button ${state.filters.openAt ? "active" : ""}"
      data-time-filter-toggle
      type="button"
      aria-haspopup="menu"
      aria-expanded="${state.timeMenuOpen ? "true" : "false"}"
    >
      ${icon(activeOption?.icon ?? icons.clock)}
      <span>${escapeHtml(activeOption?.label ?? "Время")}</span>
      <span class="chevron" aria-hidden="true">${icon(icons.chevronDown)}</span>
    </button>
    ${
      state.timeMenuOpen
        ? `<div class="time-dropdown" role="menu">
          ${timeFilterOptions
            .map(
              (option) => `<button
                class="time-option ${option.value === state.filters.openAt ? "active" : ""}"
                data-time-option="${option.value}"
                type="button"
                role="menuitem"
              >
                ${icon(option.icon)}
                <span>${escapeHtml(option.menuLabel)}</span>
              </button>`,
            )
            .join("")}
          <div class="dropdown-divider"></div>
          <button class="time-option clear" data-time-clear type="button" role="menuitem">
            ${icon(icons.x)}
            <span>Сбросить</span>
          </button>
        </div>`
        : ""
    }
  </div>`;
}

function closeTimeMenu() {
  if (!state.timeMenuOpen) return;
  state.timeMenuOpen = false;
  renderToolbar();
}

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&libraries=marker`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.append(script);
  });
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`${response.status} ${await response.text()}`);
  }
  return (await response.json()) as T;
}

function escapeHtml(input: unknown): string {
  return String(input ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escapeAttr(input: unknown): string {
  return escapeHtml(input).replaceAll("'", "&#039;");
}
